import { parseISO, getTime, format } from "date-fns";

import { panelUrl, saasModelOption } from "@gl-shared/shared/helpers";
import moment from "moment";

export const hotelPanelRootUrl = "hotel";

export const appSection = {
  dashboard: "Dashboard",
  bookings: "Bookings",
  employees: "Employees",
  addEmployeeForm: "AddEmployeeForm",
  editEmployeeForm: "EditEmployeeForm",
  logins: "Logins",
  addLoginForm: "AddLoginForm",
  editLoginForm: "EditLoginForm",
  hotelSettings: "HotelSettings",
  marketplace: "Marketplace",
  offers: "Offers",
  editOffer: "EditOffer",
  addOffer: "AddOffer"
};

export const pagesUrl = {
  login: "login/",
  resetPassword: "reset-password/",
  storeNewPassword: `reset-password/store-new-password/:token`,
  invalidToken: "reset-password/invalid-token/",
  dashboard: "",
  bookings: "bookings/",
  marketplace: "marketplace/",
  employees: "employees/",
  addEmployee: "employees/add-employee/",
  editEmployee: "employees/edit-employee/",
  offers: "offers/",
  editOffer: "offers/edit/:code/",
  newOffer: "offers/new/",
  logins: "logins/",
  addLogins: "logins/add-login/",
  editLogins: "logins/edit-login/",
  settings: "settings/"
};

// initial selected category
export const initialSelectedCategory = {
  short_name: "all",
  display_name: "All",
  priority: 999
};

export const initialHotelGroup = [
  {
    id: 0,
    name: "All Hotels"
  }
];

export const startingDateRange = {
  startDate: moment().subtract(6, "days"),
  endDate: moment()
};

export const removeTrailingSlash = str => {
  return str.replace(/\/$/, "");
};

export const delayForHidingFormMsg = 6000;

export const isAddEditOfferPageActive = currentPath => {
  // check if current path is /new or /edit Offer
  return !!(
    currentPath.includes(`/${hotelPanelRootUrl}/${pagesUrl.newOffer}`) ||
    currentPath.includes(`/${hotelPanelRootUrl}/${pagesUrl.editOffer.split(":")[0]}`)
  );
};

// if min or max amount didn't pass -> use default value (1 & 10)
export const getOptionsToSelect = (minAmount = 1, maxAmount = 10) => {
  let arr = [];
  for (let i = minAmount; i < maxAmount + 1; i++) {
    arr.push({ value: i, label: i.toString() });
  }
  return arr;
};

export const checkRedirection = responseURL => {
  if (responseURL && responseURL.includes(`${panelUrl}/login`)) {
    window.location = responseURL;
  }
};

export const generateCsvString = (titleArr, valueArr) => {
  return [titleArr, ...valueArr].map(f => f.join(",")).join("\n");
};

export const exportCsv = (titleArr, valueArr, csvFilename) => {
  const csv = generateCsvString(titleArr, valueArr);

  const csvContent = "data:text/csv;charset=utf-8," + csv;
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", csvFilename);
  document.body.appendChild(link);
  link.click();
};

export const getPlatformName = payment_status => {
  return payment_status === "paid_via_invoice"
    ? "Web App"
    : payment_status === "paid_via_hotel"
    ? "Concierge"
    : payment_status === "paid_via_invoice_mobile"
    ? "Mobile App"
    : payment_status === "paid_via_invoice_mobility"
    ? "Mobility Booker"
    : payment_status === "paid"
    ? "Guest"
    : payment_status === "paid_via_hotel_taxibooker"
    ? "Taxi Booker"
    : null;
};

export const getExportFilename = (suffix, dateRange, isRange) => {
  let formattedDate;
  isRange
    ? (formattedDate = `${dateRange?.startDate?.format("YYYYMMDD")}-${dateRange?.endDate?.format("YYYYMMDD")}`)
    : (formattedDate = `${dateRange?.format("YYYYMMDD")}`);

  return `${suffix}${formattedDate}`;
};

export const exportHotelBookingsCsv = (bookings, currency, hotelName, filename) => {
  const titles = [
    "Booking Code",
    "Booking Date",
    "Booking For Date",
    "Offer Name",
    "Provider Name",
    "Amount",
    "Customer",
    "Total Price",
    "Commission",
    "Employee",
    "Comment",
    "Platform",
    "Status"
  ];
  const bookingsValueArr = bookings.map(booking => [
    booking?.booking_code,
    format(new Date(booking?.created_at), "dd.MM.yyyy - HH:mm"),
    format(new Date(booking?.event_date), "dd.MM.yyyy") + " - " + booking?.event_time,
    booking?.event?.name,
    booking?.event?.location?.name,
    getSumOfUnitsForOffer(booking),
    booking?.external_customer_name === hotelName || booking?.external_customer_name === null
      ? booking?.customer.first_name + " " + booking?.customer.last_name
      : booking?.external_customer_name,
    currency + " " + booking?.total_price_without_fees,
    booking?.commission_booking?.total_hotel_commission
      ? `${currency} ${booking?.commission_booking?.total_hotel_commission.toFixed(2)}`
      : "-",
    booking?.employee != null ? booking?.employee.first_name : "",
    booking?.customer_comment || "",
    getPlatformName(booking?.payment_status),
    booking?.status
  ]);

  return exportCsv(titles, bookingsValueArr, `${filename}.csv`);
};

// Method for handling section loading in globalModule
export const getLoadingSectionArray = (currentSectionLoadingNames, sectionLoadingState, actionSectionLoadingName) => {
  if (sectionLoadingState === true) {
    let updatedArr = [].concat(currentSectionLoadingNames);
    updatedArr.push(actionSectionLoadingName);
    return updatedArr;
  } else if (sectionLoadingState === false) {
    let updatedArr = [].concat(currentSectionLoadingNames);
    updatedArr.splice(updatedArr.indexOf(actionSectionLoadingName), 1);
    return updatedArr;
  }
};

// Get amount of units for offer - as sum of units, for event price type "per_person", or as sum "amount_of_units", for other price types
export const getSumOfUnitsForOffer = booking => {
  let sumOfUnits = 0;
  if (booking.event_price_type === "per_person") {
    sumOfUnits += booking?.number_of_persons + booking?.number_of_children + booking?.number_of_infants;
  } else {
    sumOfUnits += booking?.amount_of_units;
  }
  return sumOfUnits;
};

export const shouldShowRefreshPageIcon = location => {
  const pagesForShowingRefreshIcon = [
    `/${hotelPanelRootUrl}/${pagesUrl.dashboard}`,
    `/${hotelPanelRootUrl}/${pagesUrl.bookings}`,
    `/${hotelPanelRootUrl}/${pagesUrl.offers}`,
    `/${hotelPanelRootUrl}/${pagesUrl.marketplace}`,
    `/${hotelPanelRootUrl}/${pagesUrl.employees}`,
    `/${hotelPanelRootUrl}/${pagesUrl.logins}`,
    `/${hotelPanelRootUrl}/${pagesUrl.settings}`
  ];

  return pagesForShowingRefreshIcon?.includes(location?.pathname);
};

export const getFutureDateFromCurrentDate = days => {
  const today = new Date();
  return new Date(today.getTime() + days * 24 * 60 * 60 * 1000);
};

export const getCookie = cname => {
  let name = cname + "=";
  let ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      // Extract and return the JWT token
      return c.substring(name.length, c.length);
    }
  }
  // Return "" if JWT cookie is not found
  return "";
};

// TODO: WRITE TEST
// Check if the token is expired
export const isTokenExpired = expiresIn => {
  if (!expiresIn) {
    return true;
  }
  const dateObject = parseISO(expiresIn);
  const milliseconds = getTime(dateObject);

  return new Date().getTime() > milliseconds;
};

export const sortHotelGroupsAlphabeticallyByName = hotelGroups => {
  return hotelGroups.sort((a, b) => {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  });
};

// If default user hotel not exists in hotel_group --> add that hotel and format the hotel_group array
// otherwise skip that part and just add initialHotelGroup on the beginning
// 'All Hotels' should always be first & others alphabetically sorted
export const getFormattedHotelGroups = (hotelGroups, defaultUserHotel) => {
  if (hotelGroups?.some(group => group?.id === defaultUserHotel?.id)) {
    const sortedHotelGroups = sortHotelGroupsAlphabeticallyByName(hotelGroups);
    return initialHotelGroup.concat(sortedHotelGroups);
  } else {
    const arrWithDefaultHotel = sortHotelGroupsAlphabeticallyByName(hotelGroups.concat(defaultUserHotel));
    return initialHotelGroup.concat(arrWithDefaultHotel);
  }
};

export const getHotelData = (isHotelGroupExists, selectedHotelFromHotelGroup, hotelData) => {
  if (isHotelGroupExists) {
    return selectedHotelFromHotelGroup;
  } else {
    return hotelData;
  }
};

export const getHotelName = selectedHotels => {
  if (selectedHotels?.length > 1) {
    return `${selectedHotels?.length} Hotels selected`;
  } else {
    return `${selectedHotels[0]?.name}`;
  }
};

export const getCommissionLabel = (commission, commissionType, currency, priceType) => {
  if (!commission || commission === 0) {
    return <span>no commission</span>;
  } else {
    if (commissionType === "relative") {
      return (
        <div>
          {commission?.toFixed(2)}% <span>commission</span>
        </div>
      );
    }
    if (commissionType === "fixed_per_unit") {
      return (
        <div>
          {currency} {commission} <span>commission {priceType}</span>
        </div>
      );
    }
    if (commissionType === "fixed_per_booking") {
      return (
        <div>
          {currency} {commission} <span>commission per booking</span>
        </div>
      );
    }
  }
};

export const shouldRenderPremiumTrialFlag = (isHotelGroupExists, defaultHotelSaasModel, selectedHotels, hotelGroup) => {
  // for non Hotel Group Manager
  if (!isHotelGroupExists && defaultHotelSaasModel === saasModelOption.hotel.premiumTrial) {
    return true;
  }
  // for Hotel Group Manager:
  // for case when "All Hotels" is selected
  else if (selectedHotels[0]?.id === 0) {
    const hotelGroupWithoutAllHotels = hotelGroup.filter(hotel => hotel.id !== 0);
    return hotelGroupWithoutAllHotels.every(hotel => hotel.saas_model === saasModelOption.hotel.premiumTrial);
  }
  // for case when one or multiple hotels are selected && when "All Hotels" is not selected
  else if (selectedHotels?.length > 0 && selectedHotels[0]?.id !== 0) {
    const selectedHotelsWithoutAllHotels = selectedHotels.filter(hotel => hotel.id !== 0);
    return selectedHotelsWithoutAllHotels.every(hotel => hotel.saas_model === saasModelOption.hotel.premiumTrial);
  } else {
    return false;
  }
};
